import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import GroupIllustration from "assets/images/Group.svg";
import {
  AccordianArrowIcon,
  Button,
  CheckBox,
  CloseIcon,
  Drawer,
  HelenaSendIcon,
  Tooltip,
} from "components";
import { ConfirmationModal } from "components/modal";
import {
  useCreateCustomerUserOnBoarding,
  useNonPersistantMainStore,
} from "controller";
import { Provider, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { addMixpanelEvent, getApiErrorMessage } from "utilz/helpers";
import { BasicInformationForm } from "./BasicInformationForm";
import { buildBatchPayload } from "./helpers";
import { PendingUserCard } from "./PendingUserCard";
import {
  applyCustomerUserBatchResultAtom,
  pendingCustomerUsersAtom,
  resetPendingCustomerUsersAtom,
} from "../../store/addCustomerUsersAtoms";

const INVITE_TOOLTIP =
  "If unchecked, the user account will still be created, but the end user won't receive an email to set their password to access customer portal and apps";

/**
 * `createCustomerUserOnBoarding` returns the WHOLE response envelope (it returns
 * `resData`, not `resData.data`), so everything below lives one level down under
 * `.data`.
 *
 * Two payload shapes are live on this endpoint:
 *  - MAU-1 (requested, see .claude/files/manual-add-user-backend-api-requirements.md):
 *    `data.summary` + `data.created[]` + `data.failed[]`.
 *  - Legacy (what the endpoint returns TODAY): a plain success envelope carrying
 *    none of those keys, or `message: "Email validation failed"` with
 *    `data.errors[]`, each carrying a `message`.
 * All are normalised to created/failed below so the drawer works either way.
 */
interface BatchCreateEnvelope {
  /** The field the api layer gates on before resolving. See `hasBatchResult`. */
  success?: boolean;
  message?: string;
  data?: {
    summary?: { created?: number; failed?: number };
    created?: Array<{ email?: string | null }>;
    failed?: Array<{ email?: string | null; reason?: string | null }>;
    errors?: Array<{ email?: string | null; message?: string | null }>;
  };
}

/**
 * The store types `mspFlags` as `Record<string, any>`, so reading the flag inline
 * leaks an `any` into `useState` and makes `prev` in the setter callback an
 * implicit `any`. Resolving it through this helper pins the value to `boolean` at
 * the boundary. `?? true` keeps the "default on when the flag has not arrived"
 * behaviour of the sibling drawer.
 */
const resolveSendInviteDefault = (
  flags: Record<string, unknown> | undefined,
): boolean => Boolean(flags?.is_customer_invite_enabled ?? true);

interface AddCustomerUsersDrawerProps {
  open: boolean;
  customerId: string;
  customerName: string;
  onBack: () => void;
  onClose: (e?: unknown, type?: "save" | "cancel") => void;
}

/**
 * Reads the pending-queue atoms from whatever jotai store is AMBIENT, so a test
 * can seed and inspect that store directly. Production must not mount this
 * directly — use `AddCustomerUsersDrawerWithStore` below, which gives the queue a
 * store scoped to the mount.
 */
export const AddCustomerUsersDrawer = ({
  open,
  customerId,
  customerName,
  onBack,
  onClose,
}: AddCustomerUsersDrawerProps) => {
  const theme = useTheme();
  const pendingUsers = useAtomValue(pendingCustomerUsersAtom);
  const applyBatchResult = useSetAtom(applyCustomerUserBatchResultAtom);
  const resetPendingUsers = useSetAtom(resetPendingCustomerUsersAtom);

  const mspFlags = useNonPersistantMainStore((state) => state.mspFlags);
  const [sendInvite, setSendInvite] = useState<boolean>(
    resolveSendInviteDefault(mspFlags),
  );
  useEffect(() => {
    setSendInvite(resolveSendInviteDefault(mspFlags));
  }, [mspFlags]);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const { mutateAsync: createUsers, isLoading: isSubmitting } =
    useCreateCustomerUserOnBoarding();

  const pendingCount = pendingUsers.length;

  const closeAndReset = (type?: "save" | "cancel") => {
    resetPendingUsers();
    onClose(undefined, type);
  };

  // Unsaved work = queued rows, or details typed but not yet added.
  const handleRequestClose = () => {
    if (pendingCount > 0 || isFormDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    closeAndReset("cancel");
  };

  const onDirtyChange = useCallback(
    (dirty: boolean) => setIsFormDirty(dirty),
    [],
  );

  const handleSubmit = async () => {
    if (pendingCount === 0) return;
    try {
      const result: BatchCreateEnvelope = await createUsers({
        customerId,
        users: buildBatchPayload(pendingUsers),
        send_invite: sendInvite,
      });

      const payload = result?.data;
      const legacyErrors = payload?.errors;
      // An EMPTY `errors[]` is how this endpoint signals success (the sibling
      // bulk-upload handler in CustomerOnBoardingDrawer.tsx guards the same way),
      // so only a NON-empty array counts as a batch result. Treating `errors: []`
      // as a result put both counts at 0 and error-toasted a batch that in fact
      // succeeded.
      const hasLegacyErrors =
        Array.isArray(legacyErrors) && legacyErrors.length > 0;
      const created = payload?.created ?? [];
      const failed =
        payload?.failed ??
        (hasLegacyErrors
          ? legacyErrors.map((error) => ({
              email: error?.email ?? null,
              reason: error?.message ?? null,
            }))
          : []);
      // The api layer only resolves when `statusCode === 200 && success === true`
      // (it throws otherwise), so reaching this point already means the batch
      // succeeded. Today's endpoint signals a plain success with an envelope that
      // carries NONE of the keys below - no `summary`, no `created[]`, no
      // `errors[]` - which is exactly what the legacy call site relies on when it
      // does `if (result.success)`. Treating that as "nothing was created" would
      // error-toast a batch that was in fact fully created and leave the user to
      // re-submit into "already exists" failures, so an unrecognised envelope is
      // read as a full success instead.
      //
      // The `message` check is NOT redundant with `errors[]`. The production call
      // site (CustomerOnBoardingDrawer.tsx:231) discriminates the legacy failure on
      // `message` alone and only then reaches for `data.errors` defensively - so a
      // validation failure can arrive with `message` set and no usable array. Left
      // to the key checks alone that envelope would look "unrecognised", be read as
      // a full success, and wipe every queued row while claiming users were created
      // that were not. Same harm as above, inverted.
      const hasBatchResult =
        payload?.summary !== undefined ||
        payload?.created !== undefined ||
        payload?.failed !== undefined ||
        hasLegacyErrors ||
        result?.message === "Email validation failed";

      // Arrays win over `summary` whenever the key is present: the row-drop set is
      // derived from `created[]`, so counting from `summary` instead could drop
      // rows while announcing that nothing was added.
      const createdCount = hasBatchResult
        ? payload?.created !== undefined
          ? created.length
          : (payload?.summary?.created ?? 0)
        : pendingCount;
      const failedCount = hasBatchResult
        ? payload?.failed !== undefined || hasLegacyErrors
          ? failed.length
          : (payload?.summary?.failed ?? 0)
        : 0;
      const total = pendingCount;

      if (createdCount > 0) {
        addMixpanelEvent({
          event: "create_user",
          data: { count: createdCount },
        });
        // A user created WITH a category has been badged — same business
        // moment as a later badge edit, so it rides the same event with
        // `action: "assigned"`. Counted off `created[]` where the endpoint
        // returns it, so a row that failed is never reported as badged; the
        // `createdCount === pendingCount` fallback covers the legacy envelope
        // that carries no arrays (see the `hasBatchResult` note above).
        const badgedCount =
          created.length > 0
            ? (() => {
                const createdEmails = new Set(
                  created
                    .map((c) => c?.email?.trim().toLowerCase())
                    .filter(Boolean),
                );
                return pendingUsers.filter(
                  (u) =>
                    Boolean(u.categoryValue) &&
                    createdEmails.has(u.email.trim().toLowerCase()),
                ).length;
              })()
            : createdCount === pendingCount
              ? pendingUsers.filter((u) => Boolean(u.categoryValue)).length
              : 0;
        if (badgedCount > 0) {
          addMixpanelEvent({
            event: "user_badge_updated",
            data: {
              action: "assigned",
              count: badgedCount,
              source: "add_users_drawer",
            },
          });
        }
      }

      applyBatchResult({ created, failed });

      if (createdCount > 0 && failedCount === 0) {
        // Only promise the e-mail when one is actually sent — with the invite
        // checkbox off the backend creates the accounts and sends nothing.
        toast.success(
          createdCount === 1
            ? sendInvite
              ? "User added successfully. they'll receive an email to set up their account."
              : "User added successfully. no invite email was sent."
            : sendInvite
              ? `${createdCount} users added successfully. they'll receive an email to set up their accounts.`
              : `${createdCount} users added successfully. no invite emails were sent.`,
        );
        closeAndReset("save");
        return;
      }

      if (createdCount > 0 && failedCount > 0) {
        toast(
          `${createdCount} of ${total} users added. ${failedCount} couldn't be added.`,
          { icon: "⚠️" },
        );
        return;
      }

      toast.error(
        failedCount === 1
          ? "Couldn't add the user."
          : "Couldn't add the users.",
      );
    } catch (e) {
      toast.error(
        getApiErrorMessage(e, "Couldn't add users. please try again."),
      );
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleRequestClose}
        drawerStyle={{ width: "957px", maxWidth: "100vw" }}
        bodySx={{
          backgroundColor: theme.palette.neutral["015"],
          padding: "8px",
          overflowY: "auto",
        }}
        header={
          <Stack
            direction="row"
            alignItems="center"
            gap="12px"
            sx={{
              height: "56px",
              padding: "0 16px",
              borderBottom: `1px solid ${theme.palette.neutral["015"]}`,
              backgroundColor: theme.palette.neutral.main,
            }}
          >
            <IconButton
              size="small"
              onClick={onBack}
              aria-label="Back to add-user options"
              sx={{
                width: "32px",
                height: "32px",
                border: `1px solid ${theme.palette.neutral["015"]}`,
              }}
            >
              <AccordianArrowIcon
                sx={{ fontSize: 14, transform: "rotate(180deg)" }}
              />
            </IconButton>
            <Typography variant="h5" sx={{ flex: 1, minWidth: 0 }}>
              {`Add users for ${customerName}`}
            </Typography>
            <IconButton
              size="small"
              onClick={handleRequestClose}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        }
        footer={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              height: "48px",
              flexShrink: 0,
              padding: "0 16px",
              borderTop: `1px solid ${theme.palette.neutral["020"]}`,
              backgroundColor: theme.palette.neutral.main,
            }}
          >
            <Tooltip title={INVITE_TOOLTIP} placement="top-start">
              <Stack direction="row" alignItems="center" gap="8px">
                <CheckBox
                  checked={sendInvite}
                  onChange={() => setSendInvite((prev) => !prev)}
                  inputProps={{ "aria-label": "Send invite e-mails" }}
                />
                <Typography variant="body-medium">
                  Send invite mail
                </Typography>
              </Stack>
            </Tooltip>
            <Stack direction="row" alignItems="center" gap="12px">
              <Button variant="secondary" onClick={handleRequestClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                endIcon={<HelenaSendIcon fontSize="small" />}
                disabled={pendingCount === 0 || isSubmitting}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {sendInvite
                  ? `Create & send invite (${pendingCount})`
                  : `Create (${pendingCount})`}
              </Button>
            </Stack>
          </Stack>
        }
      >
        <Stack
          direction="row"
          gap="16px"
          sx={{
            padding: "16px",
            borderRadius: "8px",
            backgroundColor: theme.palette.neutral.main,
            height: "100%",
            minHeight: "440px",
          }}
        >
          <BasicInformationForm
            customerId={customerId}
            onDirtyChange={onDirtyChange}
            isSubmitting={isSubmitting}
          />

          <Stack
            sx={{
              width: "520px",
              minHeight: 0,
              flexShrink: 0,
              padding: "16px",
              gap: "16px",
              borderRadius: "8px",
              border: "1px solid #E3E3E3",
              backgroundColor: "#F6F6F5",
            }}
          >
            <Typography
              variant="body-small"
              sx={{ color: theme.palette.lightBg.low, flexShrink: 0 }}
            >
              {`Ready to invite (${pendingCount})`}
            </Typography>

            {pendingCount > 0 ? (
              <Stack
                sx={{
                  flex: 1,
                  gap: "12px",
                  overflowY: "auto",
                  overflowX: "visible",
                  padding: "4px",
                  margin: "-4px",
                }}
              >
                {pendingUsers.map((user) => (
                  // flexShrink 0 is load-bearing: Collapse's root carries
                  // `min-height: 0`, which switches off the `min-height: auto`
                  // that normally stops a flex item shrinking below its
                  // content. Once the queue is tall enough to scroll, every row
                  // would shrink and — since an entered Collapse is
                  // `overflow: visible` — each card would spill over the next.
                  <Collapse
                    key={user.id}
                    in
                    appear
                    timeout={250}
                    sx={{ flexShrink: 0 }}
                  >
                    <Box
                      sx={{
                        "@keyframes userRiseIn": {
                          from: { opacity: 0, transform: "translateY(4px)" },
                          to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "userRiseIn 250ms ease-out",
                      }}
                    >
                      <PendingUserCard user={user} customerId={customerId} />
                    </Box>
                  </Collapse>
                ))}
              </Stack>
            ) : (
              <Stack
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "16px",
                  textAlign: "center",
                }}
              >
                <Box
                  component="img"
                  src={GroupIllustration}
                  alt="Invite illustration"
                  sx={{ width: "160px", height: "auto" }}
                />
                <Typography
                  variant="body-small"
                  sx={{ color: theme.palette.lightBg.low, maxWidth: "320px" }}
                >
                  Invite your users
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Drawer>

      <ConfirmationModal
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        type="warning"
        title="Discard unsaved users?"
        tagline="The users you've added and any details you've entered haven't been created yet — closing will discard them."
        primaryButtonText="Discard"
        secondaryButtonText="Keep editing"
        onPrimaryButtonClick={() => {
          setShowDiscardConfirm(false);
          closeAndReset("cancel");
        }}
        onSecondaryButtonClick={() => setShowDiscardConfirm(false)}
      />
    </>
  );
};

/**
 * The shipped entry point. The pending-queue atoms are module-level, so without
 * a `<Provider>` they live in jotai's DEFAULT store — one queue shared by the
 * whole app for its whole lifetime. Going Back to the method cards and picking a
 * DIFFERENT customer then re-entered this step with the previous customer's rows
 * still queued, carrying that customer's `site_id`s. The Provider gives each
 * mount its own store, so the queue dies with the drawer. Same pattern as
 * `shared/features/assignees/AssigneesDrawer`.
 */
export const AddCustomerUsersDrawerWithStore = (
  props: AddCustomerUsersDrawerProps,
) => (
  <Provider>
    <AddCustomerUsersDrawer {...props} />
  </Provider>
);
